import {
  AppLayout,
  BreadcrumbGroup,
  ContentLayout,
  Flashbar,
  Icon,
  Spinner,
  Button,
  Popover,
  Link,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { EventInput, EventStatus, ShiftInput } from '@amzn/red-velvet-api';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DiscardChangesModal } from '../components/events/createEvent/components/discardChangesModal';
import { useWizardToolsPanel } from '../hooks/useWizardToolsPanel';
import { EventPreviewModal } from '../components/events/createEvent/components/EventPreviewModal';
import {
  getEventDetailsStep,
  getPublishStep,
  getShiftStep,
  getTeamAndPartnershipStep,
} from '../components/events/createEvent/steps';
import { CreateEventWizard } from '../components/events/wizard/createEventWizard';
import { useUpdateEvent } from '../hooks/events/updateEvent';
import { useUnpublishEvent } from '../hooks/events/unpublishEvent';
import { useEventPrePopulation } from '../hooks/events/useEventPrePopulation';
import { useWizardStrings } from '../hooks/localization/wizardStrings';
import { useUnsavedChangesBrowserNavigationPrompt } from '../hooks/unsavedChanges';
import {
  validateEventDetails,
  validateShifts,
  validateTeamAndPartnership,
} from '../utils/eventValidation';
import { publishKatalMetric } from '../components/katalAnalytics';

export function EditEvent() {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const wizardStrings = useWizardStrings('createEventWizard', namespaces);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('id') ?? '';
  const urlFeaturesParam = searchParams.get('features');
  const viewEventUrl = `/view-event?id=${eventId}${urlFeaturesParam ? '&features=' + urlFeaturesParam : ''}`;

  const [page, setPage] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasPrePopulated, setHasPrePopulated] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [unpublishError, setUnpublishError] = useState<string | null>(null);
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [showPublishError, setShowPublishError] = useState(false);
  const [discardModalVisible, setDiscardModalVisible] = useState(false);
  const wizardTools = useWizardToolsPanel();

  useUnsavedChangesBrowserNavigationPrompt(hasUnsavedChanges);

  const {
    eventInput: prePopulatedEvent,
    shiftInputs,
    shiftIdsWithSignups,
    totalSignupCount,
    hasPastShiftsWithAttendance,
    eventStatus,
    earliestShiftIdWithSignups,
    isLoading,
    error: loadError,
  } = useEventPrePopulation(eventId || null);

  const updateEventMutation = useUpdateEvent();
  const unpublishEventMutation = useUnpublishEvent();

  const initialEventDetailsStepField: EventInput = {
    title: '',
    description: '',
    shortDescription: '',
    focusArea: '',
    primaryActivity: '',
  };
  const [eventDetailsStepField, setEventDetailsStepField] = useState<EventInput>(
    initialEventDetailsStepField
  );
  const [shiftStepField, setShiftStepField] = useState<ShiftInput[]>([]);

  // Pre-populate form when data loads — must wait for both queries to resolve
  useEffect(() => {
    if (prePopulatedEvent && !isLoading) {
      setEventDetailsStepField(prePopulatedEvent);
      setShiftStepField(shiftInputs);
      setHasPrePopulated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shiftInputs is a new ref each render; prePopulatedEvent + isLoading gate this effect
  }, [prePopulatedEvent, isLoading]);

  const validateAllSteps = (): Record<string, string> => {
    const allErrors = {
      ...validateEventDetails(eventDetailsStepField, t),
      ...validateShifts(shiftStepField, t),
      ...validateTeamAndPartnership(eventDetailsStepField, t),
    };
    setErrors(allErrors);
    return allErrors;
  };

  const updateEventFieldHandler = (args: EventInput) => {
    setEventDetailsStepField(args);
    setHasUnsavedChanges(true);
    setErrors((prev) => {
      const remaining = validateEventDetails(args, t);
      const teamRemaining = validateTeamAndPartnership(args, t);
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        if (
          !key.startsWith('shift_') &&
          key !== 'publishError' &&
          !remaining[key] &&
          !teamRemaining[key]
        ) {
          delete next[key];
        }
      }
      return next;
    });
  };

  const hasValidationErrors =
    showPublishError && Object.keys(errors).some((key) => key !== 'publishError');

  const steps = [
    getEventDetailsStep(
      t('eventDetailsStep.eventDetails'),
      t('eventDetailsStep.eventDetailsSubText'),
      eventDetailsStepField,
      updateEventFieldHandler,
      'editEvent',
      errors,
      wizardTools.openGrantPanel
    ),
    getShiftStep(
      t('shiftStep.stepTitle'),
      t('shiftStep.headerDescription'),
      shiftStepField,
      (args) => {
        setShiftStepField(args);
        setHasUnsavedChanges(true);
      },
      eventDetailsStepField,
      updateEventFieldHandler,
      'editEvent',
      errors,
      shiftIdsWithSignups,
      wizardTools.openShiftTypePanel
    ),
    getTeamAndPartnershipStep(
      t('teamPartnershipStep.stepTitle'),
      t('teamPartnershipStep.stepDescription'),
      eventDetailsStepField,
      updateEventFieldHandler,
      'editEvent',
      errors
    ),
    getPublishStep(
      t('reviewPublishStep.stepTitle'),
      t('reviewPublishStep.preview'),
      () => {
        publishKatalMetric('editEvent_previewModalOpened', eventId);
        setIsPreviewModalVisible(true);
      },
      {
        currentEventFields: eventDetailsStepField,
        currentShiftFields: shiftStepField,
        updateShiftFieldHandler: (args: ShiftInput[]) => {
          setShiftStepField(args);
          setHasUnsavedChanges(true);
        },
        updateEventFieldHandler,
        setActiveStepIndex: (index: number) => {
          setSubmitError(null);
          setUnpublishError(null);
          setPage(index);
        },
        errors,
        publicationStatus:
          eventStatus === EventStatus.UNPUBLISHED
            ? 'unpublished'
            : hasUnsavedChanges
              ? 'pending'
              : 'published',
        totalSignupCount,
        submitError,
        onDismissError: () => setSubmitError(null),
        metricPrefix: 'editEvent',
        onDismissPublishError: () =>
          setErrors((prev) => {
            const { publishError, ...rest } = prev;
            return rest;
          }),
      },
      hasValidationErrors ? t('error.resolveRequiredFields') : undefined
    ),
  ];

  if (!eventId) {
    return (
      <AppLayout
        footerSelector=".footer"
        navigationHide={true}
        toolsHide={true}
        content={
          <ContentLayout>
            <Flashbar items={[{ type: 'error', content: t('error.eventIdRequired') }]} />
          </ContentLayout>
        }
        contentType="wizard"
      />
    );
  }

  const isReady = !isLoading && (hasPrePopulated || !!loadError);
  const earlyContent = loadError ? (
    <Flashbar items={[{ type: 'error', content: loadError }]} />
  ) : !isReady ? (
    <Spinner size="large" />
  ) : null;

  return (
    <>
      <AppLayout
        footerSelector=".footer"
        navigationHide={true}
        toolsHide={wizardTools.toolsHide}
        toolsOpen={wizardTools.toolsOpen}
        onToolsChange={wizardTools.onToolsChange}
        tools={wizardTools.tools}
        breadcrumbs={
          <BreadcrumbGroup
            items={[
              {
                text: t('breadcrumbs.myActivity'),
                href: `/my-activity${urlFeaturesParam ? '?features=' + urlFeaturesParam : ''}`,
              },
              {
                text: t('breadcrumbs.coordinatorActivity'),
                href: `/coordinator-activity${urlFeaturesParam ? '?features=' + urlFeaturesParam : ''}`,
              },
              { text: t('breadcrumbs.editEvent'), href: '#' },
            ]}
          />
        }
        content={
          <ContentLayout>
            {earlyContent ?? (
              <CreateEventWizard
                metricPrefix="editEvent"
                eventId={eventId}
                submitMetricName={
                  eventStatus === EventStatus.UNPUBLISHED
                    ? 'editEvent_submitAndRepublish'
                    : 'editEvent_submit'
                }
                i18nStrings={{
                  ...wizardStrings,
                  submitButton:
                    eventStatus === EventStatus.UNPUBLISHED
                      ? t('createEventWizard.editAndPublishButton')
                      : t('createEventWizard.submitChangesButton'),
                  nextButton: t([`createEventWizard.nextButton`], {
                    title: steps[page + 1] ? steps[page + 1].title : t([`wizard.nextButton`]),
                  }),
                }}
                steps={steps}
                isLoadingNextStep={
                  updateEventMutation.isPending || unpublishEventMutation.isPending
                }
                // "Move to drafts" button: hidden when already UNPUBLISHED (no-op) or when
                // past shifts have attendance (no path back to draft). Disabled with popover
                // when signups exist. Enabled otherwise.
                secondaryActions={
                  eventStatus === EventStatus.UNPUBLISHED ||
                  hasPastShiftsWithAttendance ? undefined : totalSignupCount > 0 ? (
                    <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                      <Button disabled>{t('createEventWizard.moveToDrafts')}</Button>
                      <Popover
                        header={t('createEventWizard.moveToDrafts')}
                        content={
                          <SpaceBetween size="xs">
                            <span>
                              {t('createEventWizard.moveToDraftsDisabledReason', {
                                count: totalSignupCount,
                              })}
                            </span>
                            <Link
                              href={`/manage-signups?eventId=${eventId}&shiftId=${earliestShiftIdWithSignups}`}
                              target="_blank"
                            >
                              {t('createEventWizard.moveToDraftsManageSignupsLink')}
                            </Link>
                          </SpaceBetween>
                        }
                        triggerType="custom"
                        size="large"
                      >
                        <Icon name="status-info" />
                      </Popover>
                    </SpaceBetween>
                  ) : (
                    <Button
                      loading={unpublishEventMutation.isPending}
                      disabled={updateEventMutation.isPending}
                      onClick={() => {
                        setUnpublishError(null);
                        unpublishEventMutation.mutate(
                          {
                            eventId,
                            syncBenevity: searchParams.get('syncBenevity') === 'true' || undefined,
                          },
                          {
                            onSuccess: () => {
                              publishKatalMetric('editEvent_unpublishSuccess', eventId);
                              setHasUnsavedChanges(false);
                              navigate(viewEventUrl);
                            },
                            onError: (err) => {
                              publishKatalMetric('editEvent_unpublishFailure', eventId);
                              setUnpublishError(
                                t('error.unpublishEventError', {
                                  detail: err.message || t('error.unknownErrorDetail'),
                                })
                              );
                            },
                          }
                        );
                      }}
                    >
                      {t('createEventWizard.moveToDrafts')}
                    </Button>
                  )
                }
                onCancel={() => {
                  if (hasUnsavedChanges) {
                    setDiscardModalVisible(true);
                  } else {
                    navigate(viewEventUrl);
                  }
                }}
                onNavigate={({ detail }) => {
                  setShowPublishError(false);
                  wizardTools.close();
                  setPage(detail.requestedStepIndex);
                }}
                onSubmit={() => {
                  setSubmitError(null);
                  const allErrors = validateAllSteps();
                  if (Object.keys(allErrors).length > 0) {
                    setShowPublishError(true);
                    publishKatalMetric(
                      'editEvent_validationFailure',
                      Object.keys(allErrors).join(',')
                    );
                    setTimeout(() => {
                      const firstError = document.querySelector('[class*="awsui_error__message"]');
                      if (firstError) {
                        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      } else {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }, 0);
                    return;
                  }
                  updateEventMutation.mutate(
                    {
                      eventId,
                      event: eventDetailsStepField,
                      shifts: shiftStepField,
                      syncBenevity: searchParams.get('syncBenevity') === 'true' || undefined,
                    },
                    {
                      onSuccess: (response) => {
                        // Always update shifts with backend-assigned IDs to prevent duplicates on retry
                        setShiftStepField(response.shifts);

                        if (response.syncErrors?.length) {
                          publishKatalMetric(
                            'editEvent_partialFailure',
                            response.syncErrors.length.toString()
                          );
                          setSubmitError(
                            t('error.syncErrorsPartialFailure', {
                              errors: response.syncErrors.join('; '),
                            })
                          );
                        } else {
                          publishKatalMetric('editEvent_updateSuccess', eventId);
                          setHasUnsavedChanges(false);
                          navigate(viewEventUrl);
                        }
                      },
                      onError: (err) => {
                        publishKatalMetric('editEvent_updateFailure', eventId);
                        const msg = err.message;
                        setSubmitError(
                          !msg || msg.toLowerCase() === 'unknown error'
                            ? t('error.updateEventUnknownError')
                            : msg
                        );
                      },
                    }
                  );
                }}
                activeStepIndex={page}
                stepError={
                  unpublishError ? (
                    <Flashbar
                      items={[
                        {
                          type: 'error',
                          content: unpublishError,
                          dismissible: true,
                          onDismiss: () => setUnpublishError(null),
                        },
                      ]}
                    />
                  ) : undefined
                }
              />
            )}
          </ContentLayout>
        }
        contentType="wizard"
      />
      <DiscardChangesModal
        visible={discardModalVisible}
        onDismiss={() => setDiscardModalVisible(false)}
        onConfirm={() => {
          publishKatalMetric('editEvent_discardChanges', eventId);
          navigate(viewEventUrl);
        }}
      />
      <EventPreviewModal
        currentEventFields={eventDetailsStepField}
        currentShiftFields={shiftStepField}
        isVisible={isPreviewModalVisible}
        onDismiss={() => setIsPreviewModalVisible(false)}
        eventId={eventId}
      />
    </>
  );
}
